import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { isNil, pick, isEqual } from 'lodash';
import { useAtomValue } from 'jotai';
import { Button, Form, InputNumber, Select, Space, Popover } from 'antd';
import { CaretRightOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type UniqueIdentifier,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDimensions, useCriticalEvents } from '@src/services/queries';
import { UNIT_OPTIONS } from '@src/shared/constants/analysis';
import SettingsContainer from '@shared/components/SettingsContainer';
import InfoTooltip from '@shared/components/InfoTooltip';
import PropagationRadioGroup from '@shared/components/PropagationRadioGroup';
import propagationTooltipImg from '@src/images/tooltip-propagation.gif';
import StepSelect from './StepSelect';
import * as Styles from './styled';
import * as store from '../store';
import type { RecoveryChildrenProps } from '@src/shared/components/Recovery';
import appTracker from '@shared/utils/sensor/apptracker';

const PROPAGATION_IMG = (
  <img
    src={propagationTooltipImg}
    alt="Propagation Method Tooltip"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      transform: 'scale(1.05)',
    }}
  />
);

const PROPAGATION_CARD_BODY_STYLES: React.CSSProperties = {
  width: 450,
  height: 600,
  overflow: 'hidden',
  display: 'flex',
};

export interface AnalysisSettingProps
  extends Partial<RecoveryChildrenProps<store.DashboardSettingValue>> {}

const AnalysisSetting = (props: AnalysisSettingProps) => {
  const { cachedValues, onUpdate, onDispose } = props;
  const navigate = useNavigate();
  const rawPattern = store.useDashboardRawPattern();
  const durationLimit = store.useDashboardDurationLimit();
  const propagationSettings = store.useDashboardPropagationSettings();
  const onChange = store.useOnChange();
  const [form] = Form.useForm(useAtomValue(store.formInstanceAtom));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const { data: dimensions } = useDimensions();
  const { data: criticalEvents, isFetching: isCriticalEventsFetching } = useCriticalEvents();
  const formPatternValue = Form.useWatch('critical_events', form) ?? rawPattern;

  // Only sync propagation settings from store to form, but pick existing values in form to avoid overwrite user changes.
  React.useEffect(() => {
    form.setFieldValue('propagation_config', {
      ...propagationSettings,
      ...pick(form.getFieldValue('propagation_config'), Object.keys(propagationSettings)),
    });
  }, [form, propagationSettings]);

  // Sync changed state to `true` when there is cached values
  React.useLayoutEffect(() => {
    if (cachedValues) {
      const cached = pick(cachedValues, ['critical_events', 'timeout', 'propagation_config']);
      const currentPropagationConfig = form.getFieldValue('propagation_config');

      /**
       * NOTE THAT:
       * The value of `propagation_config` is controlled by outer store sync, there are some other components may update it.
       * Must ensure the logic of draft recovery overwrite is correct.
       *
       * A special case is that:
       * the user first modifies the propagation value of a dimension in the settings form,
       * then exits the settings form (for example, clicks Edit Dimension) and removes the dimension in the dashboard area on the right.
       * This will cause the cached form content to conflict with the actual when the user backs the settings form.
       *
       * Here we can have to pick the latest dimensions to overwrite it since we render the propagations based on the values in store,
       * and antd Form can only check the value items to remove the items not exist in latest version,
       * but cannot remove the items from form values that are not exist in the store.
       */
      form.setFieldsValue({
        ...cached,
        propagation_config: {
          ...currentPropagationConfig,
          ...pick(cached.propagation_config, Object.keys(currentPropagationConfig ?? {})),
        },
      });
    }
  }, [form, cachedValues]);

  const handleFormValuesChange = React.useCallback<
    GetProp<typeof Form<store.DashboardSettingValue>, 'onValuesChange'>
  >(
    (_, values) => {
      onUpdate?.(values);
    },
    [onUpdate],
  );

  const handleApply = React.useCallback(async () => {
    try {
      const values = await form.validateFields();

      onChange((draft) => {
        draft.critical_events = values.critical_events;
        draft.timeout.unit = values.timeout.unit;
        draft.timeout.val = values.timeout.val;
        draft.propagation_config = values.propagation_config ?? {};
      });

      onDispose?.();
    } catch {
      // noop
    }
  }, [form, onChange, onDispose]);

  const renderPatternSection = React.useCallback(() => {
    return (
      <Form.List
        name="critical_events"
        rules={[
          {
            validator: async (_, criticalEvents) => {
              if (!criticalEvents || criticalEvents.length < 1) {
                return Promise.reject(new Error('At least 1 step is required'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        {(fields, { add, remove, move }, { errors }) => {
          const handleDragEnd = (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
              return;
            }
            const oldIndex = fields.findIndex((field) => field.key === active.id);
            const newIndex = fields.findIndex((field) => field.key === over.id);
            if (oldIndex !== -1 && newIndex !== -1) {
              move(oldIndex, newIndex);
            }
          };

          return (
            <>
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={fields.map((field) => field.key as UniqueIdentifier)}
                  strategy={verticalListSortingStrategy}
                >
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {fields.map((field, index, array) => {
                      const fieldValue = form.getFieldValue(['critical_events', field.name]);
                      const isValid =
                        isNil(fieldValue) || criticalEvents?.criticalEventMap.has(fieldValue);
                      const preInvalidCount = array
                        .slice(0, index)
                        .reduce(
                          (count, f) =>
                            count +
                            (isNil(form.getFieldValue(['critical_events', f.name])) ||
                            criticalEvents?.criticalEventMap.has(
                              form.getFieldValue(['critical_events', f.name]),
                            )
                              ? 0
                              : 1),
                          0,
                        );

                      return (
                        <Styles.StepFormItem
                          key={field.key}
                          name={[field.name]}
                          rules={[
                            { required: true, message: 'Please select an event' },
                            {
                              validator: (_, value) => {
                                if (!isNil(value) && !criticalEvents?.criticalEventMap.has(value)) {
                                  return Promise.reject(
                                    new Error(
                                      'The selected event was deleted. Please choose another.',
                                    ),
                                  );
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <StepSelect
                            field={field}
                            label={isValid ? `Step ${index + 1 - preInvalidCount}` : 'Invalid'}
                            disabled={!isValid}
                            deleteDisabled={isValid && fields.length < 2}
                            options={criticalEvents?.criticalEventOptions}
                            selectProps={{
                              loading: isCriticalEventsFetching,
                            }}
                            onDelete={() => {
                              remove(field.name);
                              // tracking event
                              const eventId = form.getFieldValue(['critical_events', field.name]);
                              const eventName = criticalEvents?.criticalEventMap.get(eventId)?.name;
                              appTracker({
                                name: 'Funnel Analysis Step Deleted',
                                data: {
                                  step_number: index + 1,
                                  eventId,
                                  eventName,
                                },
                              });
                            }}
                            onEditClick={(v) => {
                              void navigate('.', {
                                state: {
                                  setting: 'event',
                                  id: v,
                                },
                                replace: true,
                              });
                            }}
                          />
                        </Styles.StepFormItem>
                      );
                    })}
                    <Form.Item>
                      <Space style={{ marginLeft: 30 }}>
                        <Button
                          variant="text"
                          color="primary"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            add();
                            // tracking event
                            appTracker({
                              name: 'Funnel Analysis Step Added',
                              data: {},
                            });
                          }}
                        >
                          Add Step
                        </Button>
                        <Form.ErrorList errors={errors} />
                      </Space>
                    </Form.Item>
                  </Space>
                </SortableContext>
              </DndContext>
            </>
          );
        }}
      </Form.List>
    );
  }, [
    sensors,
    form,
    criticalEvents?.criticalEventMap,
    criticalEvents?.criticalEventOptions,
    isCriticalEventsFetching,
    navigate,
  ]);

  const renderDurationLimitSection = React.useCallback(() => {
    return (
      <Form.Item label="Max window within">
        <Space size={6}>
          <Form.Item name={['timeout', 'val']}>
            <InputNumber min={1} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name={['timeout', 'unit']}>
            <Select style={{ width: 100 }} options={UNIT_OPTIONS} />
          </Form.Item>
        </Space>
      </Form.Item>
    );
  }, []);

  const renderPropagationSection = React.useCallback(() => {
    const entries = Object.entries(propagationSettings);

    if (entries.length === 0) {
      return <Styles.EmptyHint>No propagation settings yet.</Styles.EmptyHint>;
    }

    const stepOptions = formPatternValue
      ?.filter((item) => criticalEvents?.criticalEventMap.has(item))
      .map((item, index) => ({
        value: `step_${index + 1}`,
        label: `${index + 1} ${criticalEvents?.criticalEventMap.get(item)?.name ?? item}`,
      }));

    return (
      <Space
        direction="vertical"
        size={16}
        style={{ width: '100%' }}
        styles={{
          item: {
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: '8px 16px',
          },
        }}
      >
        {entries.map(([dimension]) => (
          <Form.Item
            key={dimension}
            name={['propagation_config', dimension]}
            label={dimensions?.dimensionMap.get(dimension)?.name || dimension}
            layout="vertical"
            rules={[
              {
                required: true,
                message: 'Please select a propagation method',
              },
              {
                validator: (_, value: Propagation) => {
                  if (value.startsWith('step_')) {
                    const stepIndex = parseInt(value.replace('step_', ''), 10);

                    if (stepIndex < 1 || stepIndex > stepOptions.length) {
                      return Promise.reject(new Error('Selected step is out of range'));
                    }
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <PropagationRadioGroup autoSelectFirstStep stepOptions={stepOptions} />
          </Form.Item>
        ))}
      </Space>
    );
  }, [propagationSettings, dimensions, formPatternValue, criticalEvents?.criticalEventMap]);

  const collapseItems = React.useMemo<GetProp<typeof Styles.Content, 'items'>>(() => {
    return [
      {
        key: 'pattern',
        label: (
          <>
            Define Funnel Steps
            <InfoTooltip title="Select critical events to define funnel steps. Drag to reorder as needed." />
          </>
        ),
        children: renderPatternSection(),
      },
      {
        key: 'durationLimit',
        label: (
          <>
            Conversion Window
            <InfoTooltip title="Maximum time allowed for users to complete all funnel steps." />
          </>
        ),
        children: renderDurationLimitSection(),
      },
      {
        key: 'propagationSettings',
        label: (
          <>
            Dimension Propagation Method
            <Popover
              placement="right"
              styles={{
                body: PROPAGATION_CARD_BODY_STYLES,
              }}
              content={PROPAGATION_IMG}
            >
              <Styles.TooltipInfoIcon />
            </Popover>
          </>
        ),
        children: renderPropagationSection(),
      },
    ];
  }, [renderPatternSection, renderDurationLimitSection, renderPropagationSection]);

  const defaultCollapseActiveKeys = React.useMemo(
    () => collapseItems.map((item) => item.key as string),
    [collapseItems],
  );

  const initialValues = React.useMemo(
    () => ({
      critical_events: rawPattern,
      timeout: durationLimit,
      propagation_config: propagationSettings,
    }),
    [rawPattern, durationLimit, propagationSettings],
  );
  const formValues = Form.useWatch([], form) ?? initialValues;

  const changed = React.useMemo(
    () => !isEqual(initialValues, formValues),
    [initialValues, formValues],
  );

  return (
    <SettingsContainer
      header={
        <>
          <Styles.HeaderTitle>Set Up Funnel Analysis</Styles.HeaderTitle>
          <Button
            variant="outlined"
            color="primary"
            iconPosition="end"
            icon={<CaretRightOutlined />}
            disabled={!changed}
            style={{ paddingRight: 8 }}
            onClick={() => void handleApply()}
          >
            Apply
          </Button>
        </>
      }
    >
      <Styles.StyledForm
        form={form}
        initialValues={initialValues}
        layout="horizontal"
        colon={false}
        onValuesChange={handleFormValuesChange}
      >
        <Styles.Content
          bordered={false}
          defaultActiveKey={defaultCollapseActiveKeys}
          items={collapseItems}
        />
      </Styles.StyledForm>
    </SettingsContainer>
  );
};

export default React.memo(AnalysisSetting);
